import React, { useState } from 'react';
import { format, isValid, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';
import { CalendarDays, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

// The API/form contract remains a local "yyyy-MM-dd'T'HH:mm" string.
// Keep date and time separate in the UI to avoid iOS Safari's large native
// datetime-local picker (and the overflowing two-column inputs).
const BookingDateTimeField = ({ id, label, value = '', onChange, defaultTime = '09:00' }) => {
  const [calendarOpen, setCalendarOpen] = useState(false);
  const datePart = value.slice(0, 10);
  const timePart = value.slice(11, 16);
  const date = /^\d{4}-\d{2}-\d{2}$/.test(datePart)
    ? parseISO(`${datePart}T12:00`)
    : null;
  const selectedDate = date && isValid(date) ? date : undefined;

  const handleDateSelect = (nextDate) => {
    if (!nextDate) return;
    onChange(`${format(nextDate, 'yyyy-MM-dd')}T${timePart || defaultTime}`);
    setCalendarOpen(false);
  };

  const handleTimeChange = (event) => {
    const nextDate = selectedDate ? datePart : format(new Date(), 'yyyy-MM-dd');
    onChange(`${nextDate}T${event.target.value}`);
  };

  return (
    <div className="min-w-0 space-y-2">
      <Label htmlFor={`${id}-date`}>{label} *</Label>
      <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_7rem] gap-2">
        <Button
          id={`${id}-date`}
          type="button"
          variant="outline"
          aria-label={`${label}datum`}
          aria-expanded={calendarOpen}
          aria-controls={`${id}-calendar`}
          onClick={() => setCalendarOpen((open) => !open)}
          className="h-11 min-w-0 justify-start gap-2 rounded-xl px-3"
        >
          <CalendarDays className="h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" />
          <span className="min-w-0 flex-1 truncate text-left">
            {selectedDate ? format(selectedDate, 'd MMM yyyy', { locale: nl }) : 'Kies datum'}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
        </Button>
        <Label htmlFor={`${id}-time`} className="sr-only">{label}tijd</Label>
        <Input
          id={`${id}-time`}
          type="time"
          value={timePart}
          onChange={handleTimeChange}
          className="h-11 min-w-0 rounded-xl px-2 text-base md:text-sm"
        />
      </div>
      {calendarOpen && (
        <div id={`${id}-calendar`} className="min-w-0 rounded-xl border border-slate-200 bg-white">
          <Calendar
            mode="single"
            selected={selectedDate}
            defaultMonth={selectedDate || new Date()}
            onSelect={handleDateSelect}
            locale={nl}
            weekStartsOn={1}
            showOutsideDays={false}
            className="mx-auto w-fit max-w-full p-2"
          />
        </div>
      )}
    </div>
  );
};

export default BookingDateTimeField;
